import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, qs } from "../client";
import type {
  NewsItem,
  NewsListQuery,
  NewsListResponse,
  NewsTag,
  NewsTagCreatePayload,
  NewsUpsertPayload,
} from "../types/news";

export function useNewsList(query?: NewsListQuery) {
  const offset = query?.offset ?? 0;
  const limit = query?.limit ?? 20;
  return useQuery({
    queryKey: ["news", "list", offset, limit],
    queryFn: () => api.get<NewsListResponse>(`/api/news${qs({ offset, limit })}`),
  });
}

export function useNewsItem(id: number | null) {
  return useQuery({
    queryKey: ["news", "item", id],
    queryFn: () => api.get<NewsItem>(`/api/news/${id}`),
    enabled: id !== null && !Number.isNaN(id),
  });
}

export function useAdminNewsList(query?: NewsListQuery) {
  const offset = query?.offset ?? 0;
  const limit = query?.limit ?? 20;
  return useQuery({
    queryKey: ["admin", "news", "list", offset, limit],
    queryFn: () => api.get<NewsListResponse>(`/api/admin/news${qs({ offset, limit })}`),
  });
}

export function useAdminNewsTags() {
  return useQuery({
    queryKey: ["admin", "news", "tags"],
    queryFn: () => api.get<NewsTag[]>("/api/admin/news/tags"),
  });
}

export function useCreateNewsTag() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: NewsTagCreatePayload) => api.post<NewsTag>("/api/admin/news/tags", body),
    onSuccess: (newTag) => {
      client.setQueryData<NewsTag[]>(["admin", "news", "tags"], (old) => {
        if (!old) return [newTag];
        if (old.some((t) => t.id === newTag.id)) return old;
        const next = [...old, newTag];
        next.sort((a, b) => a.name.localeCompare(b.name));
        return next;
      });
      void client.invalidateQueries({ queryKey: ["admin", "news", "tags"] });
    },
  });
}

export function useCreateNews() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: NewsUpsertPayload) => api.post<NewsItem>("/api/admin/news", body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin", "news"] });
      void client.invalidateQueries({ queryKey: ["news"] });
    },
  });
}

export function useUpdateNews() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: NewsUpsertPayload }) =>
      api.put<NewsItem>(`/api/admin/news/${id}`, body),
    onSuccess: (_item, { id }) => {
      client.removeQueries({ queryKey: ["news", "item", id] });
      void client.invalidateQueries({ queryKey: ["admin", "news"] });
      void client.invalidateQueries({ queryKey: ["news"] });
    },
  });
}

export function useDeleteNews() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del(`/api/admin/news/${id}`),
    onSuccess: (_data, id) => {
      client.removeQueries({ queryKey: ["news", "item", id] });
      void client.invalidateQueries({ queryKey: ["admin", "news"] });
      void client.invalidateQueries({ queryKey: ["news"] });
    },
  });
}
